import { defineStore } from 'pinia'
import { ofetch } from 'ofetch'
import { useFetchPendingRequestsStore } from './_fetchPendingRequests'
import { useRuntimeConfig, useNuxtApp } from '#app'

export const useAuthStore = defineStore('auth', {
    state: () => {
        const defaultHeaders = {
            Accept: 'application/json'
        }

        return {
            authenticated: false,
            token: null,
            user: null,
            apiHeaders: defaultHeaders,
            currentCreche: null,
            organization: null
        }
    },

    actions: {
        makeApiWrapper () {
            const runtimeConfig = useRuntimeConfig()

            const that = this

            return ofetch.create({
                retry: 0,

                baseURL: runtimeConfig.public.apiUrl,
                headers: this.apiHeaders,
                onRequest ({ options }) {
                    /**
                     * Fix Query Serialization
                     */
                    if (options.query) {
                        const query = options.query
                        Object.entries(query).forEach(([key, val]) => {
                            if (typeof val === 'object' && Array.isArray(val) && val !== null) {
                                query[key + '[]'] = val.map(v => typeof v === 'object' ? JSON.stringify(v) : v)
                                delete query[key]
                            }
                            if (val === null) {
                                delete query[key]
                            }
                        })
                    }

                    /**
                     * Keep track of the abort controller
                     * Unless explicitly set 'cancellable' to false (which can applied to some requests you don't ever want to cancel)
                     */
                    if (options.cancellable !== false) {
                        const controller = new AbortController()
                        const pendingRequests = useFetchPendingRequestsStore()
                        pendingRequests.addAbortController(controller)
                        options.signal = controller.signal
                    }
                },
                async onResponseError ({ response }) {
                    // Blob downloads (responseType: 'blob') receive JSON errors as a Blob: parse them so pageError can show the message
                    if (response._data instanceof Blob && response._data.type.includes('json')) {
                        try {
                            response._data = JSON.parse(await response._data.text())
                        } catch {
                            // not JSON after all: keep the raw Blob
                        }
                    }

                    if (response._data) {
                        response.data = response._data
                    }

                    if (response.status && response.status === 401) {
                        that.logout()
                        if (!window.location.pathname.includes('/auth')) {
                            window.location = '/auth/logout'
                        }
                    } else if (response.status && response.status === 503) {
                        window.location = '/maintenance'
                    }
                }
            })
        },

        setApiHeader (header, value) {
            this.apiHeaders[header] = value
        },

        login (payload) {
            const api = this.makeApiWrapper()

            return api('/authenticate', {
                method: 'POST',
                body: payload
            })
        },

        setToken (token) {
            this.token = token
            this.setApiHeader('Authorization', 'Bearer ' + this.token)
            localStorage.setItem('abc_auth_token', token)
            this.authenticated = true
        },

        fetchUser () {
            const api = this.makeApiWrapper()

            return api('/me')
                .then((response) => {
                    this.user = response.data
                    this.user.has_tablet_access = response.has_tablet_access
                    this.currentCreche = response.data.creches.find(c => c.uuid === this.user.current_creche_uuid)
                    this.organization = response.data.organization
                })
        },

        logout () {
            this.$reset()

            localStorage.removeItem('abc_auth_token')
            localStorage.removeItem('abc_urgency_list')

            const { $router } = useNuxtApp()

            setTimeout(() => {
                $router.push('/auth/login')
            }, 500)
        }
    },

    persist: [
        { pick: ['token', 'authenticated'] },
        // localStorage: too big for a cookie. Kept so the tablet can boot offline (urgency list)
        { pick: ['user', 'currentCreche', 'organization'], storage: localStorage }
    ]
})
